/**
 * The latest plugin file-intake list (ADR-030), held synchronously for the
 * handoff gates that cannot await (`handoffTargetsArche`), plus the one file
 * matching rule. Dependency-light on purpose — `handoff.ts` imports it, and
 * the architecture parity test loads it outside the client's module graph
 * (hence the relative import). Filled by `plugin-intake-directory.ts`, which
 * owns the fetch.
 */
import { mimeToFileKind } from '../../lib/vault-file-kinds';

/** A plugin app that can open Vault files (mirrors `FileIntakeDestination`). */
export interface PluginIntake {
  archeId: string;
  label: string;
  kinds: readonly string[];
  /** null = whole kinds; otherwise the file's extension or MIME type must be listed. */
  types?: readonly string[] | null;
}

let snapshot: ReadonlyMap<string, PluginIntake> = new Map();

export function setPluginIntakeSnapshot(list: readonly PluginIntake[]): void {
  snapshot = new Map(list.map((d) => [d.archeId, d]));
}

/** The plugin destination for `archeId`, from the latest fetch, or undefined. */
export function pluginIntakeFor(archeId: string): PluginIntake | undefined {
  return snapshot.get(archeId);
}

/**
 * Does `file` fall within an intake's effective kinds and types? Mirrors
 * `fileMatchesFileIntake` in @fias/db-types (pinned by
 * tests/architecture/plugin-file-intake-client-parity.test.ts).
 */
export function fileMatchesIntake(
  file: { mimeType: string | null; name: string },
  intake: { kinds: readonly string[]; types?: readonly string[] | null },
): boolean {
  if (!intake.kinds.includes(mimeToFileKind(file.mimeType))) return false;
  if (!intake.types) return true;
  const dot = file.name.lastIndexOf('.');
  const ext = dot > 0 && dot < file.name.length - 1 ? file.name.slice(dot).toLowerCase() : null;
  const mime = (file.mimeType ?? '').toLowerCase().split(';')[0].trim();
  return (ext !== null && intake.types.includes(ext)) || (!!mime && intake.types.includes(mime));
}
